import { ConfirmModal, Input, TextAreaAndImage, Multimedia, Select, InputSizes, SelectSizes } from '@clockout-dev/design-elements';
import { SupportTicketConstants, CATEGORY_OPTIONS, PRIORITY_OPTIONS } from '../support.enums';
import styles from '../styles/create-ticket-modal.module.scss';

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {boolean} props.submitting
 * @param {string} props.subject
 * @param {string} props.body
 * @param {Array} props.attachments
 * @param {string|null} props.category
 * @param {string|null} props.priority
 * @param {function} props.onSubjectChange
 * @param {function} props.onBodyChange
 * @param {function} props.onAttachmentsChange
 * @param {function} props.onCategoryChange
 * @param {function} props.onPriorityChange
 * @param {function} props.onCancel
 * @param {function} props.onSubmit
 * @param {string} [props.title]
 * @param {string} [props.description]
 */
function CreateTicketModal({
    open,
    submitting,
    subject,
    body,
    attachments = [],
    category,
    priority,
    onSubjectChange,
    onBodyChange,
    onAttachmentsChange,
    onCategoryChange,
    onPriorityChange,
    onCancel,
    onSubmit,
    title = 'New support ticket',
    description = 'Send the Clockout team the details we need to help with your request.',
}) {
    const subjectOk = (subject || '').trim().length > 0 && (subject || '').trim().length <= SupportTicketConstants.SUBJECT_MAX_LENGTH;
    const bodyLen = (body || '').trim().length;
    const bodyWithinLimit = bodyLen <= SupportTicketConstants.BODY_MAX_LENGTH;
    // Require both subject and body (description is mandatory, not optional even with attachments on creation)
    const bodyRequired = bodyLen > 0;
    const canSubmit = subjectOk && bodyRequired && bodyWithinLimit && !submitting;

    // ConfirmModalFooter closes when onOk returns anything other than false.
    // Parent owns `open` — success calls handleCloseCreateModal(); cancel uses onCancel.
    // Always return false so ConfirmModal never auto-closes via onOk.
    const handleSubmit = async () => {
        if (canSubmit) {
            await onSubmit();
        }
        return false;
    };

    return (
        <ConfirmModal
            title={title}
            description={description}
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            okText={submitting ? 'Submitting...' : 'Submit ticket'}
            cancelText="Cancel"
            confirmLoading={submitting}
            okButtonProps={{ disabled: !canSubmit }}
            destroyOnHidden
            classNames={{
                body: styles.modalBody,
                footer: styles.modalFooter,
            }}
        >
            <div className={styles.fields}>
                <Input
                    size={InputSizes.SMALL}
                    id="supportTicketSubject"
                    label="Subject"
                    placeholder="Brief summary"
                    maxLength={SupportTicketConstants.SUBJECT_MAX_LENGTH}
                    value={subject}
                    onChange={(e) => onSubjectChange(e.target.value)}
                    showCount
                />
                {
                    onCategoryChange && onPriorityChange ? (
                        <div className={styles.selectContainer}>
                            <Select
                                size={SelectSizes.SMALL}
                                id="supportTicketCategory"
                                placeholder="Select category"
                                label="Category"
                                options={CATEGORY_OPTIONS}
                                value={category}
                                onChange={(value) => onCategoryChange(value)}
                            />
                            <Select
                                size={SelectSizes.SMALL}
                                id="supportTicketPriority"
                                placeholder="Select priority"
                                label="Priority"
                                options={PRIORITY_OPTIONS}
                                value={priority}
                                onChange={(value) => onPriorityChange(value)}
                            />
                        </div>
                    ) : null
                }
                <div>
                    <TextAreaAndImage
                        id="supportTicketMessage"
                        size={InputSizes.SMALL}
                        placeholder="Describe your question or issue"
                        rows={6}
                        maxLength={SupportTicketConstants.BODY_MAX_LENGTH}
                        value={body}
                        onChange={(e) => onBodyChange(e.target.value)}
                        onFilesChange={(entries) => onAttachmentsChange(entries)}
                        showButton
                        showCount
                    />
                    {attachments.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                            {attachments.map((att, i) => (
                                <Multimedia
                                    key={`${att.name}-${i}`}
                                    src={att.url}
                                    name={att.name}
                                    type={att.type}
                                    onRemove={() => onAttachmentsChange(attachments.filter((_, idx) => idx !== i))}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </ConfirmModal>
    );
}

export default CreateTicketModal;
